/**
 * Integrytas Group - Base App (MOBILE)
 * Page: UDC.LAYOUT — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/UdcLayoutPage.tsx. That desktop
 * console lets an admin pick a DSE-enabled entity + host screen + workspace and
 * decide, per custom (user-defined) field, where it lands on that screen — tab /
 * section / order / column-span / control override — and whether it shows on the
 * form, in the grid and in the filter bar. This is a DISTINCT presentation (dense
 * card list + placement facet chips + bottom-sheet placement editor) over the SAME
 * data/logic layer only:
 *   - reads via apiDb (dse_entity, udc_host, dse_attribute_def, udc_field_layout) —
 *     exactly the tables/columns the desktop reads,
 *   - every write routes through the Common Routine (routedInsert / routedUpdate) —
 *     §2 single write path; never a direct apiDb write. The desktop upsert becomes
 *     an insert when no layout row exists yet, else a change by its primary key.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Layers } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'UDC.LAYOUT';

// ---------------------------------------------------------------------------
// Row shapes — mirror the desktop's dse_attribute_def + udc_field_layout join.
// ---------------------------------------------------------------------------

interface EntityRow { entity_name: string }
interface HostRow { page_code: string; label: string | null }
interface AttrRow { dse_attribute_def_id: number; attribute_key: string; label: string | null; data_type: string }

interface LayoutRow {
  udc_field_layout_id?: number;
  dse_attribute_def_id: number;
  workspace_code: string;
  page_code: string;
  tab_code: string | null;
  section_code: string | null;
  display_order: number;
  col_span: number;
  control_override: string | null;
  placeholder: string | null;
  help_text: string | null;
  is_visible: 'Y' | 'N';
  show_in_grid: 'Y' | 'N';
  show_in_filter: 'Y' | 'N';
}

type MergedField = AttrRow & { layout: LayoutRow };

type PlacementFacet = 'all' | 'form' | 'grid' | 'filter';

// Control-override choices — labelled constants so displayed labels go through
// i18n (§17.B11), never bare UPPER_CASE literals in the JSX. The stored value stays
// the code; only the label is localized. '' means "auto" (derive from data type).
const CONTROL_OPTIONS: { value: string; labelKey: string; fallback: string }[] = [
  { value: '', labelKey: 'mobile.udc.control.auto', fallback: '(auto)' },
  { value: 'TEXT', labelKey: 'mobile.udc.control.TEXT', fallback: 'Text' },
  { value: 'TEXTAREA', labelKey: 'mobile.udc.control.TEXTAREA', fallback: 'Text area' },
  { value: 'NUMBER', labelKey: 'mobile.udc.control.NUMBER', fallback: 'Number' },
  { value: 'DATE', labelKey: 'mobile.udc.control.DATE', fallback: 'Date' },
  { value: 'DATETIME', labelKey: 'mobile.udc.control.DATETIME', fallback: 'Date & time' },
  { value: 'CHECKBOX', labelKey: 'mobile.udc.control.CHECKBOX', fallback: 'Checkbox' },
  { value: 'SELECT', labelKey: 'mobile.udc.control.SELECT', fallback: 'Select' },
  { value: 'RADIO', labelKey: 'mobile.udc.control.RADIO', fallback: 'Radio' },
  { value: 'MODAL_LOOKUP', labelKey: 'mobile.udc.control.MODAL_LOOKUP', fallback: 'Modal lookup' },
];

function defaultLayout(attrId: number, workspace: string, pageCode: string): LayoutRow {
  return {
    dse_attribute_def_id: attrId,
    workspace_code: workspace,
    page_code: pageCode,
    tab_code: null,
    section_code: null,
    display_order: 0,
    col_span: 6,
    control_override: null,
    placeholder: null,
    help_text: null,
    is_visible: 'N', // a field appears on a screen only once ticked here
    show_in_grid: 'N',
    show_in_filter: 'N',
  };
}

// ---------------------------------------------------------------------------
// List screen
// ---------------------------------------------------------------------------

export function MobileUdcLayoutPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<string[]>([]);
  const [entity, setEntity] = useState('');
  const [pages, setPages] = useState<HostRow[]>([]);
  const [page, setPage] = useState('');
  const [ws, setWs] = useState('');
  const [rows, setRows] = useState<MergedField[]>([]);
  const [filter, setFilter] = useState('');
  const [facet, setFacet] = useState<PlacementFacet>('all');
  const [selected, setSelected] = useState<MergedField | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // DSE-enabled entities (mount only).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('dse_entity')
        .select('entity_name')
        .eq('is_enabled', 'Y')
        .order('entity_name');
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      setEntities(((data ?? []) as EntityRow[]).map((r) => r.entity_name));
    })();
    return () => { cancelled = true; };
  }, []);

  // Candidate host screens for the chosen entity.
  useEffect(() => {
    if (!entity) { setPages([]); setPage(''); return; }
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('udc_host')
        .select('page_code, label')
        .eq('entity_name', entity)
        .order('label');
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      const list = (data ?? []) as HostRow[];
      setPages(list);
      setPage((prev) => (list.some((h) => h.page_code === prev) ? prev : (list[0]?.page_code ?? '')));
    })();
    return () => { cancelled = true; };
  }, [entity]);

  const loadFields = async (ent: string, workspace: string, pageCode: string) => {
    if (!ent || !pageCode) { setRows([]); return; }
    setBusy(true);
    setError(null);
    try {
      const a = await apiDb
        .from('dse_attribute_def')
        .select('dse_attribute_def_id, attribute_key, label, data_type')
        .eq('entity_name', ent)
        .eq('is_active', 'Y')
        .order('sort_order')
        .order('attribute_key');
      if (a.error) throw a.error;
      const attrs = (a.data ?? []) as AttrRow[];
      const ids = attrs.map((x) => x.dse_attribute_def_id);

      let layouts: LayoutRow[] = [];
      if (ids.length) {
        const l = await apiDb
          .from('udc_field_layout')
          .select('udc_field_layout_id, dse_attribute_def_id, workspace_code, page_code, tab_code, section_code, display_order, col_span, control_override, placeholder, help_text, is_visible, show_in_grid, show_in_filter')
          .in('dse_attribute_def_id', ids)
          .eq('workspace_code', workspace)
          .eq('page_code', pageCode);
        if (l.error) throw l.error;
        layouts = (l.data ?? []) as LayoutRow[];
      }

      setRows(attrs.map((at) => ({
        ...at,
        layout: layouts.find((x) => x.dse_attribute_def_id === at.dse_attribute_def_id)
          ?? defaultLayout(at.dse_attribute_def_id, workspace, pageCode),
      })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Reload fields whenever the entity, workspace or host screen changes.
  useEffect(() => { void loadFields(entity, ws, page); }, [entity, ws, page]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((m) => {
      if (facet === 'form' && m.layout.is_visible !== 'Y') return false;
      if (facet === 'grid' && m.layout.show_in_grid !== 'Y') return false;
      if (facet === 'filter' && m.layout.show_in_filter !== 'Y') return false;
      if (!q) return true;
      return m.attribute_key.toLowerCase().includes(q) || (m.label ?? '').toLowerCase().includes(q);
    });
  }, [rows, filter, facet]);

  const facets: { key: PlacementFacet; label: string }[] = [
    { key: 'all', label: t('mobile.udc.facet.all', 'All') },
    { key: 'form', label: t('mobile.udc.facet.form', 'On form') },
    { key: 'grid', label: t('mobile.udc.facet.grid', 'In grid') },
    { key: 'filter', label: t('mobile.udc.facet.filter', 'In filter') },
  ];

  const pageLabel = (code: string) => pages.find((p) => p.page_code === code)?.label ?? code;

  const placementSummary = (l: LayoutRow) => {
    if (l.is_visible !== 'Y') return t('mobile.udc.not_on_form', 'Not on form');
    const parts: string[] = [];
    if (l.tab_code) parts.push(l.tab_code);
    if (l.section_code) parts.push(l.section_code);
    const where = parts.join(' / ');
    return where
      ? t('mobile.udc.on_form_at', 'On form · {{where}}', { where })
      : t('mobile.udc.on_form', 'On form');
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.udc.title', 'Field Placement')}
        description={t('mobile.udc.description', 'Choose where each custom field lands on a screen, and whether it shows in the grid and filter.')}
      />

      {/* Scope selectors — which screen + workspace we are laying out. */}
      <div className="m-fieldcard">
        <div className="m-formrow">
          <label htmlFor="m-udc-entity">{t('mobile.udc.entity', 'Entity')}</label>
          <select id="m-udc-entity" className="m-sel" value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">{t('mobile.udc.select_entity', 'Select entity…')}</option>
            {entities.map((en) => <option key={en} value={en}>{en}</option>)}
          </select>
        </div>
        <div className="m-formrow">
          <label htmlFor="m-udc-page">{t('mobile.udc.screen', 'Screen')}</label>
          <select
            id="m-udc-page"
            className="m-sel"
            value={page}
            disabled={!entity || pages.length === 0}
            onChange={(e) => setPage(e.target.value)}
          >
            {pages.length === 0 && <option value="">{t('mobile.udc.no_hosts', '(no screens host this entity)')}</option>}
            {pages.map((p) => <option key={p.page_code} value={p.page_code}>{p.label ?? p.page_code}</option>)}
          </select>
        </div>
        <div className="m-formrow">
          <label htmlFor="m-udc-ws">{t('mobile.udc.workspace', 'Workspace')}</label>
          <input
            id="m-udc-ws"
            className="m-inp"
            value={ws}
            onChange={(e) => setWs(e.target.value)}
            placeholder={t('mobile.udc.workspace_placeholder', 'blank = all workspaces')}
          />
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.udc.search', 'Search fields')}
          aria-label={t('mobile.udc.search', 'Search fields')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => facet === v}
        onSelect={(v) => setFacet(v as PlacementFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {!entity ? (
        <MobileContentCard><div className="m-empty"><Layers /> {t('mobile.udc.pick_entity', 'Pick a DSE-enabled entity and screen to place its custom fields.')}</div></MobileContentCard>
      ) : pages.length === 0 ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.udc.no_hosts_body', 'No screens host this entity’s custom fields yet.')}</div></MobileContentCard>
      ) : filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.udc.none', 'No fields match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((m) => (
            <MobileCardRow
              key={m.dse_attribute_def_id}
              lead={<Layers />}
              leadTone={m.layout.is_visible === 'Y' ? 'green' : 'slate'}
              title={m.label || m.attribute_key}
              subtitle={`${m.attribute_key} · ${m.data_type}`}
              trailing={
                <MobileStatusPill tone={m.layout.is_visible === 'Y' ? 'ok' : 'no'}>
                  {m.layout.is_visible === 'Y' ? t('mobile.udc.on', 'On') : t('mobile.udc.off', 'Off')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(m)}
            />
          ))}
        </MobileContentCard>
      )}

      <MobileUdcLayoutEditSheet
        session={session}
        field={selected}
        pageLabel={selected ? pageLabel(selected.layout.page_code) : ''}
        placementSummary={placementSummary}
        onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); void loadFields(entity, ws, page); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Placement editor — tab / section / order / span / control + form/grid/filter
// toggles for one field, saved through the Common Routine (§2). Missing layout
// rows are inserted; existing ones are changed by their primary key.
// ---------------------------------------------------------------------------

interface MobileUdcLayoutEditSheetProps {
  session: AuthSession;
  field: MergedField | null;
  pageLabel: string;
  placementSummary: (l: LayoutRow) => string;
  onClose: () => void;
  onSaved: () => void;
}

function MobileUdcLayoutEditSheet({ session, field, pageLabel, placementSummary, onClose, onSaved }: MobileUdcLayoutEditSheetProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [tab, setTab] = useState('');
  const [section, setSection] = useState('');
  const [order, setOrder] = useState(0);
  const [span, setSpan] = useState(6);
  const [control, setControl] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [helpText, setHelpText] = useState('');
  const [onForm, setOnForm] = useState(false);
  const [inGrid, setInGrid] = useState(false);
  const [inFilter, setInFilter] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different field is opened.
  useEffect(() => {
    if (!field) return;
    const l = field.layout;
    setTab(l.tab_code ?? '');
    setSection(l.section_code ?? '');
    setOrder(l.display_order);
    setSpan(l.col_span);
    setControl(l.control_override ?? '');
    setPlaceholder(l.placeholder ?? '');
    setHelpText(l.help_text ?? '');
    setOnForm(l.is_visible === 'Y');
    setInGrid(l.show_in_grid === 'Y');
    setInFilter(l.show_in_filter === 'Y');
    setError(null);
  }, [field]);

  const save = async () => {
    if (!field) return;
    setBusy(true);
    setError(null);

    const patch: Record<string, unknown> = {
      tab_code: tab.trim() || null,
      section_code: section.trim() || null,
      display_order: order,
      col_span: span,
      control_override: control || null,
      placeholder: placeholder.trim() || null,
      help_text: helpText.trim() || null,
      is_visible: onForm ? 'Y' : 'N',
      show_in_grid: inGrid ? 'Y' : 'N',
      show_in_filter: inFilter ? 'Y' : 'N',
    };

    // §2: route through the Common Routine (forensic stamp + audit + hooks). The
    // routine stamps modified_by; existing rows change by PK, new rows insert.
    const l = field.layout;
    const { error: e } = l.udc_field_layout_id
      ? await routedUpdate(session, 'udc_field_layout', 'udc_field_layout_id', l.udc_field_layout_id, patch)
      : await routedInsert(session, 'udc_field_layout', {
        dse_attribute_def_id: l.dse_attribute_def_id,
        workspace_code: l.workspace_code,
        page_code: l.page_code,
        ...patch,
      });

    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = field !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.udc.edit_sheet', 'Field placement')}>
        <div className="m-grabber" />
        {field && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Layers size={20} /></span>
              <span>
                <div className="m-nm">{field.label || field.attribute_key}</div>
                <div className="m-em">{field.attribute_key} · {pageLabel}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow m-formrow-ro">
                <label>{t('mobile.udc.current', 'Current placement')}</label>
                <div className="m-formrow-ro">{placementSummary(field.layout)}</div>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.udc.show_on_form', 'Show on form')}</div>
                  <div className="m-formrow-s">{t('mobile.udc.show_on_form_hint', 'Field appears on the edit form')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={onForm}
                  className={'m-switch ' + (onForm ? 'm-on' : 'm-off')}
                  onClick={() => setOnForm((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.udc.show_in_grid', 'Show in grid')}</div>
                  <div className="m-formrow-s">{t('mobile.udc.show_in_grid_hint', 'Field appears as a list column')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={inGrid}
                  className={'m-switch ' + (inGrid ? 'm-on' : 'm-off')}
                  onClick={() => setInGrid((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.udc.show_in_filter', 'Show in filter')}</div>
                  <div className="m-formrow-s">{t('mobile.udc.show_in_filter_hint', 'Field appears as a filter facet')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={inFilter}
                  className={'m-switch ' + (inFilter ? 'm-on' : 'm-off')}
                  onClick={() => setInFilter((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-udc-tab">{t('mobile.udc.tab', 'Tab')}</label>
                <input id="m-udc-tab" className="m-inp" value={tab} onChange={(e) => setTab(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-udc-section">{t('mobile.udc.section', 'Section')}</label>
                <input id="m-udc-section" className="m-inp" value={section} onChange={(e) => setSection(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-udc-order">{t('mobile.udc.order', 'Display order')}</label>
                <input
                  id="m-udc-order"
                  type="number"
                  className="m-inp"
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-udc-span">{t('mobile.udc.span', 'Column span (1–12)')}</label>
                <input
                  id="m-udc-span"
                  type="number"
                  min={1}
                  max={12}
                  className="m-inp"
                  value={span}
                  onChange={(e) => setSpan(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-udc-control">{t('mobile.udc.control_lbl', 'Control')}</label>
                <select id="m-udc-control" className="m-sel" value={control} onChange={(e) => setControl(e.target.value)}>
                  {CONTROL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-udc-placeholder">{t('mobile.udc.placeholder', 'Placeholder')}</label>
                <input id="m-udc-placeholder" className="m-inp" value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-udc-help">{t('mobile.udc.help_text', 'Help text')}</label>
                <input id="m-udc-help" className="m-inp" value={helpText} onChange={(e) => setHelpText(e.target.value)} />
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
